import React from "react";
import { badgeForDays } from "./data";

export function AvailBadge({ days, status }) {
  if (status === "immediate") {
    return <span data-testid="avail-badge" className="avail-chip avail-green"><Dot /> Immediate joiner</span>;
  }
  const b = badgeForDays(days);
  const cls = b.color === "green" ? "avail-green" : b.color === "yellow" ? "avail-yellow" : "avail-orange";
  return <span data-testid="avail-badge" className={`avail-chip ${cls}`}><Dot /> {b.label}</span>;
}

function Dot() {
  return <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />;
}

export function MatchRing({ score, size = 44 }) {
  return (
    <div className="ring" style={{ ["--p"]: score, ["--size"]: `${size}px` }}>
      <span>{score}%</span>
    </div>
  );
}

export function Chip({ children, tone = "default" }) {
  const tones = {
    default: "bg-[var(--paper-2)] text-[var(--ink-2)]",
    dark: "bg-[var(--ink)] text-[var(--paper)]",
    accent: "bg-[#FFE7DE] text-[#8A2A0F]",
    mint: "bg-[var(--brand-3)] text-[var(--brand)]",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function Section({ title, right, children, className = "" }) {
  return (
    <section className={className}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-display text-xl">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Logo({ small }) {
  return (
    <div className="flex items-center gap-2 select-none">
      <div className={`${small ? "w-7 h-7" : "w-9 h-9"} rounded-lg bg-[var(--brand)] grid place-items-center relative overflow-hidden`}>
        <div className="absolute inset-0 bg-[var(--brand-2)] opacity-80" style={{clipPath: "polygon(0 60%, 100% 40%, 100% 100%, 0 100%)"}}/>
        <span className="relative font-display text-white text-lg font-bold">tx</span>
      </div>
      <div className="font-display text-lg leading-none">
        <span className="font-bold">TalentX</span><span className="text-[var(--brand-2)] font-bold">pres</span>
      </div>
    </div>
  );
}

export function Progress({ value }) {
  return (
    <div className="w-full h-1.5 bg-[var(--paper-2)] rounded-full overflow-hidden">
      <div className="h-full bg-[var(--brand-2)] transition-all" style={{ width: `${value}%` }} />
    </div>
  );
}

// simple inline icons (avoids adding lucide bundle churn)
export const Icon = {
  Home: (p) => <svg {...p} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 10.5 12 3l9 7.5V21H3z"/><path d="M9 21v-6h6v6"/></svg>,
  Search: (p) => <svg {...p} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>,
  Briefcase: (p) => <svg {...p} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  User: (p) => <svg {...p} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6"/></svg>,
  Bell: (p) => <svg {...p} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6z"/><path d="M10 20a2 2 0 0 0 4 0"/></svg>,
  Filter: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 5h18M6 12h12M10 19h4"/></svg>,
  Arrow: (p) => <svg {...p} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>,
  Check: (p) => <svg {...p} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="m5 12 5 5L20 7"/></svg>,
  Bookmark: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 3h12v18l-6-4-6 4z"/></svg>,
  Grid: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  Users: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="9" cy="8" r="3.5"/><path d="M2 20c1-3.5 4-5 7-5s6 1.5 7 5"/><circle cx="17" cy="8" r="3"/><path d="M22 20c-.7-2.6-2.8-4.2-5-4.6"/></svg>,
  Building: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="3" width="16" height="18"/><path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2"/></svg>,
  Chart: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 20V10M10 20V4M16 20v-8M22 20H2"/></svg>,
  Settings: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3.9a7 7 0 0 0-2-1.2L14 3h-4l-.6 2.5a7 7 0 0 0-2 1.2l-2.3-.9-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-.9a7 7 0 0 0 2 1.2L10 21h4l.6-2.5a7 7 0 0 0 2-1.2l2.3.9 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z"/></svg>,
  Plus: (p) => <svg {...p} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>,
  Close: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6 6 18"/></svg>,
  ChevronLeft: (p) => <svg {...p} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 6-6 6 6 6"/></svg>,
  Location: (p) => <svg {...p} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 21s7-7 7-12a7 7 0 1 0-14 0c0 5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>,
  Clock: (p) => <svg {...p} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  Money: (p) => <svg {...p} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/></svg>,
  Star: (p) => <svg {...p} width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="m12 2 3 6.5 7 .8-5.2 4.9L18 21l-6-3.5L6 21l1.2-6.8L2 9.3l7-.8z"/></svg>,
  Sparkle: (p) => <svg {...p} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3v6M12 15v6M3 12h6M15 12h6M6 6l3 3M15 15l3 3M18 6l-3 3M9 15l-3 3"/></svg>,
};

export function Avatar({ name, size = 40, tone }) {
  // Use DiceBear notionists (real-looking illustrated avatars)
  const seed = encodeURIComponent(name);
  return (
    <div style={{ width: size, height: size, background: tone || "#EEE8D6", flexShrink: 0 }} className="rounded-full overflow-hidden border border-[var(--line)]">
      <img
        alt={name}
        src={`https://api.dicebear.com/7.x/notionists/svg?seed=${seed}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf,d1f0d1`}
        width={size} height={size} className="w-full h-full object-cover"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    </div>
  );
}

export function CompanyLogo({ name, size = 44 }) {
  const seed = encodeURIComponent(name);
  return (
    <div style={{ width: size, height: size, flexShrink: 0 }} className="rounded-lg overflow-hidden bg-[var(--ink)] border border-[var(--line)]">
      <img
        alt={name}
        src={`https://api.dicebear.com/7.x/shapes/svg?seed=${seed}&backgroundColor=0F3D2E,14B87A,0B0F14,253143,FF6A3D`}
        width={size} height={size} className="w-full h-full object-cover"
      />
    </div>
  );
}
